// Tarif-Hinweise ohne Anfrage an den Server: Knöpfe für Funktionen, die der Tarif nicht enthält, zeigen gleich das
// Fenster „Mehr mit Billtoro“ (UpgradeDialog.tsx) statt eines Fehlers. Texte wie der Server (billtoro/tarif.ts
// FEATURE_TEXT), damit es gleich klingt, egal ob die Oberfläche vorher prüft oder der Server ablehnt.
import type { BezahlPlan, PlanId, Tarif, TarifFehler, TarifFeature, TarifGrenze } from './types';

// exportiert für den Wächtertest server/test/billtoro-flags.test.ts (gleiche Schlüssel wie auf dem Server)
export const FEATURE_TEXT: Record<TarifFeature, string> = {
  erechnung: 'E-Rechnungen',
  export: 'Der Export',
  mahnungen: 'Mahnungen',
  mahnlauf: 'Automatische Mahnläufe',
  waehrung: 'Rechnungen in Fremdwährung',
  logo: 'Logo, Farbe, Vorlagen und Briefpapier',
  datev: 'Der DATEV-Export',
  kontoauszug: 'Der Abgleich mit dem Kontoauszug',
  kundenimport: 'Der Import von Kunden und Lieferanten',
  steuerbuero: 'Versand und Zugang fürs Steuerbüro',
  abos: 'Wiederkehrende Rechnungen',
  ki: 'Die KI',
  post: 'Der Versand per Post',
  team: 'Das Team',
  ohneWerbung: 'Rechnungen ohne Hinweis auf Billtoro',
  ausgaben: 'Ausgaben',
  ustva: 'Die Umsatzsteuer-Voranmeldung',
  zeiten: 'Die Zeiterfassung',
  kontaktdateien: 'Das Ablegen von Dateien bei Kunden und Lieferanten',
  lager: 'Das Lager',
  bank: 'Die Verbindung zur Bank',
};

// „… gibt es ab dem Tarif Start“ braucht den Akkusativ: „Den Import von Kunden und Lieferanten gibt es …“ (wie der
// Server, tarif.ts gibtEs; exportiert für den Wächtertest billtoro-flags.test.ts)
export const gibtEs = (feature: TarifFeature) => `${FEATURE_TEXT[feature].replace(/^Der /, 'Den ')} gibt es`;

// Was der nötige Tarif bringt, als Satzende nach „Mit Start (<Preis aus tarif.tarife> / Monat zzgl. 19 % USt) …“ im
// Fenster „Mehr mit …“ (UpgradeDialog.tsx); das Team baut das Fenster selbst (mit der Zahl der Logins). Der Wächtertest
// billtoro-flags.test.ts verlangt einen Satz für jede Funktion, die erst ein bezahlter Tarif bringt.
export const BRINGT: Partial<Record<TarifFeature, string>> = {
  mahnungen: 'mahnen Sie in allen Stufen.',
  mahnlauf: 'erinnert und mahnt Billtoro überfällige Rechnungen von selbst.',
  waehrung: 'schreiben Sie Rechnungen, Angebote und Aufträge in US-Dollar, Franken, Pfund und 10 weiteren Währungen.',
  logo: 'stehen Ihr Logo und Ihre Farbe auf jedem neuen Beleg, in einer von vier Vorlagen, auf Wunsch mit Ihrem eigenen Briefpapier.',
  datev: 'exportieren Sie Ihre Rechnungen für DATEV und Ihr Steuerbüro.',
  kontoauszug: 'gleichen Sie Zahlungen mit dem Kontoauszug ab.',
  kundenimport: 'importieren Sie Ihre Kunden und Lieferanten aus einer Liste.',
  steuerbuero: 'gehen Ihre Rechnungen jeden Monat von selbst ans Steuerbüro, und es lädt sie auf Wunsch selbst herunter.',
  abos: 'schreiben sich wiederkehrende Rechnungen von selbst.',
  ausgaben: 'erfassen Sie Ausgaben mit Beleg, führen das Kassenbuch und sehen Ihren Gewinn in der Einnahmen-Überschuss-Rechnung.',
  ustva: 'richten Sie die Umsatzsteuer-Voranmeldung ein und halten jede Abgabe in Billtoro fest.',
  ki: 'liest die KI Stundenzettel, auch viele auf einmal, und Kontoauszüge als PDF oder Foto und macht daraus Vorschläge, die Sie prüfen.',
  ohneWerbung: 'gehen Ihre Rechnungen ohne Billtoro-Hinweis hinaus.',
  zeiten: 'erfassen Sie Stunden mit der Uhr oder von Hand und übernehmen sie in die Rechnung.',
  kontaktdateien: 'legen Sie Verträge, Fotos und PDFs direkt beim Kunden oder Lieferanten ab.',
  lager: 'führt Billtoro Ihren Bestand: Zugänge, Zählung, und Rechnungen buchen ihn ab.',
  bank: 'kommen Ihre Zahlungseingänge jeden Tag von selbst, ohne Hochladen.',
};

const PLAENE: PlanId[] = ['kostenlos', 'start', 'pro'];

export const planName = (tarif: Tarif, id: PlanId) => tarif.tarife.find((t) => t.id === id)?.name ?? id;

// Fehlt die Funktion im Tarif, der gerade gilt? null = vorhanden; 'bald' = gibt es auf Billtoro noch nicht (kein
// Tarifwechsel hilft, z. B. Brief oder KI); sonst der kleinste Tarif, der sie enthält.
export function fehlendeFunktion(tarif: Tarif | null | undefined, feature: TarifFeature): BezahlPlan | 'bald' | null {
  if (!tarif || tarif.features[feature]) return null;
  if (tarif.demnaechst.some((d) => d.id === feature)) return 'bald';
  // Im Tarif enthalten, aber die Instanz kann es noch nicht
  if (tarif.tarife.find((t) => t.id === tarif.effektiv)?.features[feature]) return 'bald';
  const ab = PLAENE.find((p) => p !== 'kostenlos' && tarif.tarife.find((t) => t.id === p)?.features[feature]);
  return (ab as BezahlPlan | undefined) ?? 'bald';
}

// Dieselbe Antwort, die der Server bei 402/503 schickt
export function tarifFehlerFuer(tarif: Tarif, feature: TarifFeature): TarifFehler | null {
  const fehlt = fehlendeFunktion(tarif, feature);
  if (!fehlt) return null;
  if (fehlt === 'bald') return { error: `${FEATURE_TEXT[feature]} kommt bald.`, code: 'bald', details: { feature } };
  const wo = fehlt === 'pro' ? 'im Tarif Pro' : `ab dem Tarif ${planName(tarif, fehlt)}`;
  return {
    error: `${gibtEs(feature)} ${wo}.`,
    code: 'tarif_feature',
    details: { feature, plan: tarif.effektiv, noetig: fehlt },
  };
}

// Fenster „Mehr mit Billtoro“ für diese Funktion öffnen (vorher ein eigenes offenes Fenster schließen: es kann nur
// eines zugleich offen sein, components/Dialog.tsx). Gibt false zurück, wenn die Funktion im Tarif ist.
export function upgradeAnbieten(tarif: Tarif | null | undefined, feature: TarifFeature): boolean {
  const f = tarif ? tarifFehlerFuer(tarif, feature) : null;
  if (!f) return false;
  window.dispatchEvent(new CustomEvent('nt:tarif', { detail: f }));
  return true;
}

// Grenze erreicht (z. B. 5 von 5 Rechnungen)? Dieselbe Antwort wie der Server (tarif.ts pruefeGrenze), null = nicht
// erreicht oder unbegrenzt
export function grenzeFehler(tarif: Tarif, grenze: TarifGrenze): TarifFehler | null {
  const limit = tarif.grenzen[grenze];
  if (limit === null || tarif.nutzung[grenze] < limit) return null;
  // kleinster größerer Tarif, der mehr erlaubt
  const ab = PLAENE.indexOf(tarif.effektiv);
  const noetig =
    (PLAENE.slice(ab + 1).find((p) => {
      const g = tarif.tarife.find((t) => t.id === p)?.grenzen[grenze];
      return g === null || (g !== undefined && g > limit);
    }) as BezahlPlan | undefined) ?? null;
  const eins = limit === 1;
  const was =
    grenze === 'nutzer'
      ? eins
        ? '1 Login'
        : `${limit} Logins`
      : grenze === 'abos'
        ? `${limit} ${eins ? 'wiederkehrende Rechnung' : 'wiederkehrende Rechnungen'}`
        : `${limit} ${grenze === 'rechnungenMonat' ? (eins ? 'Rechnung' : 'Rechnungen') : eins ? 'Angebot' : 'Angebote'} im Monat`;
  const mehr = { nutzer: 'mehr Logins', abos: 'mehr wiederkehrende Rechnungen', rechnungenMonat: 'mehr Rechnungen', angeboteMonat: 'mehr Angebote' }[grenze];
  const sind = eins ? 'ist' : 'sind';
  const name = planName(tarif, tarif.effektiv);
  return {
    error: noetig
      ? `Im Tarif ${name} ${sind} ${was} enthalten. Für ${mehr} wechseln Sie bitte zum Tarif ${planName(tarif, noetig)}.`
      : `Im Tarif ${name} ${sind} höchstens ${was} möglich.`,
    code: 'tarif_grenze',
    details: { grenze, limit, plan: tarif.effektiv, noetig },
  };
}

// Fenster „Mehr mit Billtoro“ öffnen, wenn die Grenze erreicht ist; false = noch Platz
export function grenzeAnbieten(tarif: Tarif | null | undefined, grenze: TarifGrenze): boolean {
  const f = tarif ? grenzeFehler(tarif, grenze) : null;
  if (!f) return false;
  window.dispatchEvent(new CustomEvent('nt:tarif', { detail: f }));
  return true;
}
